/** 历史标题提取任务：目标语言标签解析仍可能用到（独立 titles 导航未动） */
import i18n from '@/i18n';

export const AI_COPYWRITING_TITLE_LANGUAGE_OPTIONS = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: '英文' },
  { value: 'ja', label: '日文' },
  { value: 'de', label: '德文' },
  { value: 'fr', label: '法文' },
];

/** 历史存库的中文语种标签 → 标准语言代码（仅用于展示本地化） */
const COPYWRITING_LANGUAGE_LABEL_TO_CODE = {
  '中文': 'zh-CN',
  '中文（简）': 'zh-CN',
  '简体中文': 'zh-CN',
  '中文（繁）': 'zh-TW',
  '繁体中文': 'zh-TW',
  '英文': 'en',
  '英语': 'en',
  '德文': 'de',
  '德语': 'de',
  '法文': 'fr',
  '法语': 'fr',
  '日文': 'ja',
  '日语': 'ja',
  '韩文': 'ko',
  '韩语': 'ko',
};

/**
 * 展示层把语种代码/历史中文标签翻成当前语言。
 * 存库值仍保持原样，避免影响后端与历史任务。
 */
export function localizeCopywritingLanguageLabel(codeOrLabel) {
  const raw = String(codeOrLabel || '').trim();
  if (!raw) return raw;
  const code = COPYWRITING_LANGUAGE_LABEL_TO_CODE[raw] || raw;
  const key = `workbench:studioChrome.opt.lang.${code}`;
  const translated = i18n.t(key, { defaultValue: '' });
  if (translated) return translated;
  if (code === 'zh') return i18n.t('workbench:studioChrome.opt.lang.zh-CN', { defaultValue: raw });
  return raw;
}

export function getTitleLanguageLabel(code) {
  const found = AI_COPYWRITING_TITLE_LANGUAGE_OPTIONS.find((o) => o.value === code);
  return found?.label || code || '中文';
}

/** AI 文案：输出语种（与后端 AiCopywritingService 保持一致） */
export const AI_COPYWRITING_LANGUAGE_OPTIONS = [
  { value: 'zh-CN', label: '中文（简）' },
  { value: 'zh-TW', label: '中文（繁）' },
  { value: 'en', label: '英文' },
  { value: 'de', label: '德语' },
  { value: 'fr', label: '法语' },
  { value: 'ja', label: '日文' },
  { value: 'ko', label: '韩文' },
];

export const DEFAULT_AI_COPYWRITING_LANGUAGE = 'zh-CN';

export function getCopywritingLanguageLabel(code) {
  const found = AI_COPYWRITING_LANGUAGE_OPTIONS.find((o) => o.value === code);
  return found?.label || code || '中文（简）';
}

/** 标题字数上限合法范围（与快捷弹窗一致） */
export const AI_COPYWRITING_MAX_CHARS_LIMIT = 500;

/**
 * 解析标题字数上限；空值 / 非法值表示不限制。
 * @param {unknown} raw
 * @returns {number|undefined}
 */
export function normalizeCopywritingMaxChars(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.max(1, Math.min(AI_COPYWRITING_MAX_CHARS_LIMIT, Math.floor(n)));
}

/**
 * 「字数限制」输入框内容清洗：仅保留数字，最多 3 位；空串表示不限制。
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeCopywritingMaxCharsInput(raw) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, 3);
}

/**
 * 将字数限制拼接到文案模板内容，供带入工作室「参数设置」prompt。
 * @param {string} templateContent
 * @param {unknown} maxChars
 * @param {number} [maxLength=500]
 * @returns {string}
 */
export function appendCopywritingMaxCharsToPrompt(
  templateContent,
  maxChars,
  maxLength = AI_COPYWRITING_MAX_CHARS_LIMIT,
) {
  const base = String(templateContent || '').trim();
  const limit = normalizeCopywritingMaxChars(maxChars);
  const cap = Math.max(1, Math.floor(Number(maxLength) || AI_COPYWRITING_MAX_CHARS_LIMIT));
  if (!limit) return base.slice(0, cap);
  const suffix = `\n标题字数限制：${limit}字`;
  if (!base) return suffix.trim().slice(0, cap);
  const room = Math.max(0, cap - suffix.length);
  return `${base.slice(0, room).trimEnd()}${suffix}`.slice(0, cap);
}

/** 识别历史 AI文案任务中的「标题提取」结果（新任务不再写入该 mode） */
export function isTitleExtractCopywritingResult(result) {
  return String(result?.generationMode || '').toLowerCase() === 'titleextract';
}

/** 从任务 result 解析输出语种（图生文案 language；历史标题提取可能用 targetLanguage） */
export function resolveCopywritingLanguageInfo(result) {
  const r = result && typeof result === 'object' ? result : {};
  const isTitle = isTitleExtractCopywritingResult(r);
  const code = String(
    r.language || (isTitle ? r.targetLanguage : '') || DEFAULT_AI_COPYWRITING_LANGUAGE,
  ).trim() || DEFAULT_AI_COPYWRITING_LANGUAGE;
  const savedLabel = String(r.languageLabel || '').trim();
  const label = savedLabel || (isTitle ? getTitleLanguageLabel(code) : getCopywritingLanguageLabel(code));
  return { code, label, isTitle };
}

const SCHEME_HEAD = /^[\s【]*(?:方案\s*)?([一二三四1-4])(?:】)?[\s:：·\-—]*/;

const SCHEME_SPLIT = /(?=^[\s【]*(?:方案\s*)?[一二三四1-4](?:】)?[\s:：·\-—])/m;

/**
 * 图生文案：直接剔除 * # 及其全角变体，不做 Markdown 语义解析，避免漏网。
 * 与后端 AiCopywritingService.stripMarkdownArtifacts 保持一致。
 */
const HASH_ASTERISK_PATTERN = /[#*＊＃]/g;

function cleanCopywritingLine(line) {
  return String(line || '')
    .replace(HASH_ASTERISK_PATTERN, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function sanitizeCopywritingContent(text) {
  const raw = String(text || '');
  if (!raw.trim()) return raw.trim();

  return raw
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => cleanCopywritingLine(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 将模型返回的整段文案拆成多条（按「方案一/方案二」等分隔）。
 * @returns {{ index: number, title: string, body: string }[]}
 */
export function parseCopywritingVariants(content, expectedCount = 1, options = {}) {
  const { sanitize = true } = options;
  let text = String(content || '').trim();
  if (!text) return [];
  if (sanitize) {
    text = sanitizeCopywritingContent(text);
  }

  const parts = text.split(SCHEME_SPLIT).map((p) => p.trim()).filter(Boolean);
  const variants = (parts.length > 1 ? parts : [text]).map((part, i) => {
    const headMatch = part.match(SCHEME_HEAD);
    const body = headMatch ? part.replace(SCHEME_HEAD, '').trim() : part;
    const numMap = { 一: 1, 二: 2, 三: 3, 四: 4 };
    const rawNum = headMatch?.[1];
    const index = rawNum && numMap[rawNum] ? numMap[rawNum] : (Number(rawNum) || i + 1);
    return {
      index,
      title: expectedCount > 1 ? `方案 ${index}` : '生成文案',
      body,
    };
  });

  const limit = Math.max(1, Math.min(4, expectedCount || 1));
  return variants.slice(0, limit);
}

const SECTION_DEFINITIONS = [
  { key: 'title', display: '商品标题', labels: ['商品标题', '产品标题', '标题', 'listing title', 'product title', 'title'] },
  { key: 'bullets', display: '五点描述', labels: ['五点描述', '五点卖点', 'bullet points', 'bullets', '卖点描述', '核心卖点'] },
  { key: 'description', display: '商品描述', labels: ['商品描述', '产品描述', '描述', 'product description', 'description'] },
  { key: 'detail', display: '详情介绍', labels: ['商品详情介绍', '详情介绍', '详细介绍', '商品详情', '详情', 'product details', 'detail'] },
  { key: 'highlights', display: '核心亮点', labels: ['核心亮点', '亮点', 'core highlights', 'highlights', 'key highlights'] },
  { key: 'specs', display: '规格参数', labels: ['规格参数', '产品规格', '规格', 'specifications', 'specs', 'parameters'] },
];

const PREAMBLE_LINE = /^以下(?:是|为).*(?:文案|copywriting)/i;
const SECTION_DIVIDER = /^[-—_=]{3,}\s*$/;

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 去掉括号内英文副标题、尾部冒号，便于匹配「商品标题 (Product Title)」类标题行 */
function normalizeSectionCandidate(text) {
  return String(text || '')
    .replace(/[#*＊＃]/g, '')
    .replace(/\s*[（(][^）)]*[）)]/g, '')
    .replace(/\s*[：:]\s*$/, '')
    .trim();
}

function matchSectionHeader(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed || SECTION_DIVIDER.test(trimmed)) return null;
  if (PREAMBLE_LINE.test(trimmed)) return null;

  const withoutDecor = trimmed.replace(/[#*＊＃]/g, '').trim();
  const bracketMatch = withoutDecor.match(/^【([^】]+)】\s*[：:]?\s*(.*)$/);
  const rawCandidate = bracketMatch ? bracketMatch[1].trim() : withoutDecor;
  const bracketInline = bracketMatch ? bracketMatch[2].trim() : '';
  const normalized = normalizeSectionCandidate(rawCandidate);

  if (!normalized) return null;

  for (const def of SECTION_DEFINITIONS) {
    const sortedLabels = [...def.labels].sort((a, b) => b.length - a.length);
    for (const label of sortedLabels) {
      const inlineRegex = new RegExp(`^${escapeRegex(label)}\\s*[：:]\\s*(.+)$`, 'iu');
      const inlineMatch = normalized.match(inlineRegex);
      if (inlineMatch) {
        return { key: def.key, label: def.display, inlineContent: inlineMatch[1].trim() };
      }
      const headerRegex = new RegExp(`^${escapeRegex(label)}\\s*$`, 'iu');
      if (headerRegex.test(normalized)) {
        return { key: def.key, label: def.display, inlineContent: bracketInline };
      }
    }
  }
  return null;
}

function pushSection(sections, key, label, body) {
  const text = String(body || '').trim();
  if (!text) return;
  const existing = sections.find((item) => item.key === key);
  if (existing) {
    existing.body = `${existing.body}\n\n${text}`.trim();
    return;
  }
  sections.push({
    id: key,
    key,
    label,
    body: text,
  });
}

/**
 * 将单套文案拆成「标题 / 五点描述 / 详情介绍 / 核心亮点 / 规格参数」等小节。
 * 无法识别小节时返回单段「全文」。
 * @returns {{ id: string, key: string, label: string, body: string }[]}
 */
export function parseCopywritingSections(body) {
  const text = String(body || '').trim();
  if (!text) return [];

  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const sections = [];
  let current = null;
  let seenHeader = false;

  const flush = () => {
    if (!current) return;
    pushSection(sections, current.key, current.label, current.lines.join('\n'));
    current = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || SECTION_DIVIDER.test(trimmed)) continue;

    const header = matchSectionHeader(line);
    if (header) {
      flush();
      seenHeader = true;
      current = {
        key: header.key,
        label: header.label,
        lines: header.inlineContent ? [header.inlineContent] : [],
      };
      continue;
    }

    if (current) {
      current.lines.push(line);
      continue;
    }

    if (!seenHeader && PREAMBLE_LINE.test(trimmed)) continue;

    if (trimmed) {
      current = { key: 'other', label: '其他', lines: [line] };
    }
  }
  flush();

  const named = sections.filter((item) => item.key !== 'other');
  const others = sections.filter((item) => item.key === 'other');

  if (named.length === 0) {
    const fallback = others.map((item) => item.body).join('\n\n').trim() || text;
    return [{ id: 'full', key: 'full', label: '全文', body: fallback }];
  }

  return named;
}
